import { DashboardItemService } from 'app/overview/dashboard/dashboard_item.service';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { View } from 'app/shared/collections/view';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { ImageUrlService } from 'app/shared/image_url.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { IndicatorManagerProxy } from 'app/views/accessories/IndicatorManagerProxy';
import { BaseViewController, MapBottomChartDefinition } from 'app/views/BaseViewController';
import {
    createIndicatorManagerForView,
    getBottomChartOptionsForStatType,
    getCollectionNameFromItemTypeDetails,
    getItemTypeDetailsFromViewTables,
} from 'app/views/view_types/view_utils';
import { cloneDeep, flatten, uniq } from 'lodash';
import { ImsObjectVisibilityService } from '../../../shared/visibility/ImsObjectVisibilityService';
import { SelectionService } from 'app/shared/selection/SelectionService';

export class GenericViewController extends BaseViewController {
    private readonly _dashboardItemService: DashboardItemService;
    private readonly _clockHandler: ClockHandler;

    public indicatorManager: IndicatorManagerProxy;
    private _selectionService: SelectionService;

    constructor(
        assetOptionsService: MapOptionsService,
        zoneOptionsService: MapOptionsService,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        imageUrlService: ImageUrlService,
        dashboardItemService: DashboardItemService,
        assetCollection: AssetCollection,
        zoneCollection: ZoneCollection,
        clockHandler: ClockHandler,
        imsObjectVisibilityService: ImsObjectVisibilityService,
        selectionService: SelectionService,
        centerPanelTags: Array<string> | null = null,
    ) {
        super(
            assetOptionsService,
            zoneOptionsService,
            imageUrlService,
            imsObjectVisibilityService,
            assetCollection,
            zoneCollection,
            imsItemTypeRegistry,
        );
        this._dashboardItemService = dashboardItemService;
        this._assetCollection = assetCollection;
        this._clockHandler = clockHandler;
        this._selectionService = selectionService;

        // set our center panel tags
        if (centerPanelTags) {
            // if our center panel tags were passed in, then use them
            this.centerPanelTags = centerPanelTags;
        } else {
            // if no center panel tags were passed in, then use the default which is set in the base
            //   class constructor, and add default behaviour for generic views which is to also have heat maps
            this.centerPanelTags.push('centerHeatMap');
        }

        this._dashboardItemService.onDashboardItemServiceChange(() => {
            // TODO this is a bug lol
            if (!this._imsView) {
                return;
            }
            // rebuild this thing again as it may have changed after the dashboard item service was changed
            this.mapBottomChartOptions = GenericViewController.buildBottomChartDefinitions(
                this._imsView,
                this._imsItemTypeRegistry,
                this._dashboardItemService,
            );
        });

        // we make a indicator manager proxy so we can swap out the indicator manager during run time,
        // but not ever have a null pointer to this.indicatorManager
        this.indicatorManager = new IndicatorManagerProxy();
    }

    /**
     * Builds a list of 3 bottom chart definition objects using options created from all the view tables
     * @param view
     * @param imsItemTypeRegistry
     * @param dashboardItemService
     */
    static buildBottomChartDefinitions(
        view: View,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        dashboardItemService: DashboardItemService,
    ): Array<MapBottomChartDefinition> {
        const imsItemTypeDetails = getItemTypeDetailsFromViewTables(view, imsItemTypeRegistry);
        const bottomChartOptions = flatten(
            Object.entries(imsItemTypeDetails).map(([statName, statDetails]) =>
                getBottomChartOptionsForStatType(
                    statDetails.statDefinition,
                    statName,
                    dashboardItemService,
                ),
            ),
        );
        const mapBottomChart = { options: bottomChartOptions };
        return [cloneDeep(mapBottomChart), cloneDeep(mapBottomChart), cloneDeep(mapBottomChart)];
    }

    setUpView() {
        // the super method will set up the standard map options
        super.setUpView();

        this.indicatorManager.proxyTo(
            createIndicatorManagerForView(
                this._imsView,
                this._imsItemTypeRegistry,
                this._assetCollection,
                this._clockHandler,
                this._assetOptionsService,
                this._imageUrlService,
                this._selectionService,
            ),
        );

        // get all the ims item type details for this view
        const imsItemTypeDetails = getItemTypeDetailsFromViewTables(
            this._imsView,
            this._imsItemTypeRegistry,
        );

        // update our active collections to the base list, plus the collections specified in view
        const dynamicCollectionNames = Object.values(imsItemTypeDetails).map(
            getCollectionNameFromItemTypeDetails,
        );
        this.activeCollections = uniq([
            'LocationStatCollection',
            'TriggerStatCollection',
            ...dynamicCollectionNames,
        ]);

        this.mapBottomChartOptions = GenericViewController.buildBottomChartDefinitions(
            this._imsView,
            this._imsItemTypeRegistry,
            this._dashboardItemService,
        );

        this.addImsVisualisers(imsItemTypeDetails);

        return this.assetImsVisualisers;
    }

    stopView() {
        super.stopView();
        this.indicatorManager.destroy();
    }
}
